// src/hooks/useSwipeToDismiss.ts
// 底部 Sheet 向下滑動關閉手勢

import { useRef, useEffect, useCallback } from 'react';

interface UseSwipeToDismissOptions {
  onDismiss: () => void;
  /** 觸發 dismiss 的最小距離（px），預設 120 */
  threshold?: number;
  /** 觸發 dismiss 的最小速度（px/ms），預設 0.5 */
  velocityThreshold?: number;
}

interface UseSwipeToDismissReturn {
  sheetRef: React.RefObject<HTMLDivElement>;
  backdropRef: React.RefObject<HTMLDivElement>;
}

/**
 * 底部 Sheet 向下 swipe 關閉手勢
 *
 * 邏輯：
 * - 垂直向下為主導才觸發，水平主導時 pass（不干擾 sheet 內的水平滾動）
 * - sheet 內有 scrollable 子元素且未在頂部時，不觸發（避免和 scroll 衝突）
 * - 超過 threshold 距離或速度夠快，觸發 onDismiss
 * - 未達條件，spring back 回原位
 * - backdrop 透明度跟著拖動距離淡出
 */
const useSwipeToDismiss = ({
  onDismiss,
  threshold = 120,
  velocityThreshold = 0.5,
}: UseSwipeToDismissOptions): UseSwipeToDismissReturn => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // touch 狀態用 ref，避免 useEffect stale closure 問題
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isDraggingRef = useRef(false);
  const directionLockedRef = useRef(false);

  // onDismiss 用 ref 保持最新版本，避免 useEffect 依賴觸發重新綁定
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

  // 判斷觸摸起點是否在可滾動元素內且未在頂部（scroll 衝突保護）
  const isTouchOnScrolledContent = useCallback((target: EventTarget | null): boolean => {
    if (!target || !sheetRef.current) return false;
    let el = target as HTMLElement;
    while (el && el !== sheetRef.current) {
      const { overflowY } = window.getComputedStyle(el);
      const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
      if (isScrollable && el.scrollTop > 0) return true;
      el = el.parentElement as HTMLElement;
    }
    return false;
  }, []);

  // 直接操作 DOM，避免 React re-render 造成拖動卡頓
  const applyDrag = useCallback((dragY: number) => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!sheet) return;

    const clamped = Math.max(0, dragY);
    sheet.style.transform = `translateY(${clamped}px)`;
    sheet.style.transition = 'none';

    if (backdrop) {
      const progress = Math.min(clamped / threshold, 1);
      backdrop.style.opacity = String(1 - progress * 0.6);
      backdrop.style.transition = 'none';
    }
  }, [threshold]);

  const springBack = useCallback(() => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!sheet) return;
    sheet.style.transition = 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)';
    sheet.style.transform = 'translateY(0)';
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.3s ease';
      backdrop.style.opacity = '1';
    }
  }, []);

  const dismiss = useCallback(() => {
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!sheet) return;
    const height = sheet.offsetHeight;
    sheet.style.transition = 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)';
    sheet.style.transform = `translateY(${height}px)`;
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.28s ease';
      backdrop.style.opacity = '0';
    }
    setTimeout(() => onDismissRef.current(), 280);
  }, []);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (isTouchOnScrolledContent(e.target)) return;
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      isDraggingRef.current = false;
      directionLockedRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // 方向未鎖定前，判斷主導方向
      if (!directionLockedRef.current) {
        if (absDeltaY < 5 && absDeltaX < 5) return; // 移動太少，等等再判斷
        if (absDeltaY > absDeltaX && absDeltaY > 8) {
          // 垂直主導：鎖定為 swipe to dismiss
          directionLockedRef.current = true;
          isDraggingRef.current = true;
        } else {
          // 水平主導：放棄此次手勢，讓事件冒泡（不攔截）
          touchStartRef.current = null;
          return;
        }
      }

      if (!isDraggingRef.current) return;
      if (e.cancelable) e.preventDefault();
      applyDrag(deltaY);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current || !isDraggingRef.current) {
        touchStartRef.current = null;
        isDraggingRef.current = false;
        directionLockedRef.current = false;
        return;
      }

      const touch = e.changedTouches[0];
      const deltaY = touch.clientY - touchStartRef.current.y;
      const elapsed = Math.max(1, Date.now() - touchStartRef.current.time);
      const velocity = deltaY / elapsed; // px/ms

      touchStartRef.current = null;
      isDraggingRef.current = false;
      directionLockedRef.current = false;

      if (deltaY >= threshold || velocity >= velocityThreshold) {
        dismiss();
      } else {
        springBack();
      }
    };

    const handleTouchCancel = () => {
      touchStartRef.current = null;
      isDraggingRef.current = false;
      directionLockedRef.current = false;
      springBack();
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [applyDrag, dismiss, isTouchOnScrolledContent, springBack, threshold, velocityThreshold]);

  return { sheetRef, backdropRef };
};

export default useSwipeToDismiss;
